<i18n src="./resources/locales.json"></i18n>

<template>
  <section class="ecs-incoming-interactions-container">
    <v-expansion-panels v-model="open" multiple accordion>
      <v-expansion-panel
        v-for="(interaction, index) in pendingInteractions"
        :key="interaction.interactionId"
        :class="{
          blink: !isWaitingEvent(interaction) && !interaction.isPreview,
          surface: isWaitingEvent(interaction) || interaction.isPreview
        }"
      >
        <!-- Interactions Header / Collapsed View -->
        <v-expansion-panel-header class="d-flex flex-row">
          <div class="interaction-title">
            <v-icon class="mr-2">
              {{ getInteractionTitleIcon(interaction) }}
            </v-icon>
            <!-- eslint-disable-next-line -->
            <span v-html="getInteractionTitle(interaction)" />
            <!-- eslint-disable-next-line -->
            <div v-if="interaction.isTelephony && !interaction.isDirectCall && !interaction.isPreview" class="title-additional-info" v-html="$t('answerInYourDevice')" />
            <div v-if="interaction.isPreview" class="preview-container">
              <div class="audio-preview">
                <div class="preview-label">
                  {{ $t('previewMessage') }}
                </div>
                <audio
                  controls
                  :autoplay="preferencesSettings.autoPlayRecordings"
                  :src="recordingFile(interaction)"
                  :dark="$vuetify.theme.dark"
                  :class="{ 'preview-audio': interaction.isPreview }"
                >
                  Your browser does not support the
                  <code>audio</code> element
                </audio>
              </div>
              <div class="preview-transcription">
                <span v-once class="prop-label">{{ $t('audioTranscription') }}</span>
                <span class="prop-value">
                  {{ recordingTranscription(interaction) }}
                </span>
              </div>
            </div>
          </div>
          <div class="header-information">
            <!-- Show Buttons when collapsed -->
            <div v-show="!open.includes(index)" class="collapsed-actions">
              <v-tooltip v-if="interaction.callbackRecordingFile" top style="z-index: 999">
                <template v-slot:activator="{ on }">
                  <v-icon class="px-3" v-on="on">mdi-voicemail</v-icon>
                </template>
                <span v-once>{{ $t('voiceMail') }}</span>
              </v-tooltip>
              <v-btn
                color="error"
                role="button"
                icon
                :loading="isWaitingDecline(interaction)"
                :disabled="isWaitingEvent(interaction) || isOfflineModeEnabled"
                @click="declineInteraction(interaction)"
              >
                <v-icon class="icon">
                  {{ getInteractionDeclineIcon(interaction) }}
                </v-icon>
              </v-btn>
              <v-btn
                v-if="
                  useBrowserToAnswer || !interaction.isTelephony || (interaction.isTelephony && isHPBXAndAcceptEnabled)
                "
                color="success"
                role="button"
                :loading="isWaitingAccept(interaction)"
                :disabled="isWaitingEvent(interaction) || isOfflineModeEnabled"
                icon
                @click="acceptInteraction(interaction)"
              >
                <v-icon class="icon">{{ getInteractionAcceptIcon(interaction) }}</v-icon>
              </v-btn>
            </div>
            <v-progress-circular
              v-if="!interaction.isPendingConsult && !interaction.isInternal && !interaction.isPreview"
              v-show="getInteractionRemainingTime(interaction, interactionTimers[interaction.interactionId]) > 0"
              class="timeout-visualizer"
              :rotate="-90"
              :size="32"
              :width="3"
              :value="
                (100 / interaction.timing.noAnswerTimeout) *
                  getInteractionRemainingTime(interaction, interactionTimers[interaction.interactionId])
              "
              :dark="$vuetify.theme.dark"
            >
              {{ getInteractionRemainingTime(interaction, interactionTimers[interaction.interactionId]) }}
            </v-progress-circular>
            <div v-else-if="interaction.isPreview">
              <div class="preview-label-timer">
                <v-progress-circular
                  v-if="interaction.previewTime"
                  class="timer"
                  :size="28"
                  :width="3"
                  :rotate="-90"
                  :value="100 - (previewTimer * 100) / interaction.previewTime"
                >
                  {{ previewTimer }}
                </v-progress-circular>
                <v-progress-circular v-else class="timer" :size="28" :width="3" indeterminate />
                <div class="label">
                  {{ $t('previewTimeout') }}
                </div>
              </div>
            </div>
          </div>
        </v-expansion-panel-header>
        <!-- Interactions Body Content / Expanded View --->
        <v-expansion-panel-content eager>
          <section class="interaction-content">
            <div class="primary-content">
              <div class="primary-display-name">
                {{ interaction | displayName }}
                <v-btn
                  v-show="loggedInAgent.crm === 'Enabled' && interaction.crm.contactId"
                  x-small
                  :text="showCrmInfo"
                  :outlined="!showCrmInfo"
                  class="show-crm-btn"
                  :class="{ primary: showCrmInfo }"
                  @click="showCrmInfo = !showCrmInfo"
                >
                  {{ $t('actions.crmInfo') }}
                </v-btn>
                <v-btn
                  v-show="interaction.interactionVariables"
                  x-small
                  :text="showInteractionVariables"
                  :outlined="!showInteractionVariables"
                  class="show-int-details-btn"
                  :class="{ primary: showInteractionVariables }"
                  @click="showInteractionVariables = !showInteractionVariables"
                >
                  {{ $t('actions.intDetails') }}
                </v-btn>
              </div>
              <div v-if="showQueueWaitTime(interaction)">
                <span v-once class="prop-label">{{ $t('queueWaitTime') }}</span>
                <span class="prop-value">{{ formatWaitTime(interaction.timing.waitingTime) }}</span>
              </div>
              <div v-show="interaction.crm.remarks && interaction.crm.remarks.length">
                <span v-once class="prop-label">{{ $t('remarks') }}:</span>
                <span class="prop-value">{{ interaction.crm.remarks.join(',') }}</span>
              </div>
              <div v-show="interaction.routing && interaction.routing.routingInfo">
                <span v-once class="prop-label">{{ $t('routingInfo') }}:</span>
                <span class="prop-value">{{ interaction.routing.routingInfo }}</span>
              </div>
              <EcsQuickCrmContactInfo
                v-if="showCrmInfo && interaction.crm.contactId && interaction.routing.businessProcessId"
                :contact-id="interaction.crm.contactId"
                :business-process-id="interaction.routing.businessProcessId"
              />
              <EcsQuickInteractionDetails
                v-if="showInteractionVariables"
                :business-process-id="interaction.routing.businessProcessId"
                :interaction-variables="interaction.interactionVariables"
              />
            </div>
            <div class="secondary-content">
              <div class="demands">
                <v-chip
                  v-for="demand in interaction.parsedDemands"
                  :key="demand"
                  class="mr-2 mb-2"
                  color="accent"
                  small
                >
                  {{ demand }}
                </v-chip>
              </div>
              <div class="actions">
                <v-overflow-btn
                  v-if="interaction.isCallback"
                  :items="postponeOptionsList(interaction.interactionId, interaction.routing.businessProcessId)"
                  class="postpone"
                  role="button"
                  solo
                  dense
                  segmented
                  hide-details
                  :dark="$vuetify.theme.dark"
                  :value="selectedPostponeId"
                  :loading="postponeLoading || isWaitingPostponeEvent"
                  :disabled="postponeLoading || isWaitingPostponeEvent"
                  @change="postponeOptionId => postponeInteraction(interaction.interactionId, postponeOptionId)"
                />
                <v-overflow-btn
                  v-if="completionStatusList(interaction).length"
                  :items="completionStatusList(interaction)"
                  class="disposeInteraction"
                  role="button"
                  solo
                  dense
                  segmented
                  hide-details
                  :dark="$vuetify.theme.dark"
                  :value="completionStatusList(interaction)[0].value"
                  :loading="setDispositionCodeLoading"
                  :disabled="setDispositionCodeLoading || isWaitingPostponeEvent"
                  @change="dispositionCode => setPendingStatus(interaction.interactionId, dispositionCode.name)"
                />
                <v-btn
                  v-if="interaction.isDirectCall"
                  class="decline-button bold-face mr-4"
                  color="error !important"
                  role="button"
                  @click="declineDirectCallInteraction(interaction)"
                >
                  <v-icon class="icon">{{ getInteractionDeclineIcon(interaction) }}</v-icon>
                  <span v-once>{{ $t('actions.decline') }}</span>
                </v-btn>
                <v-btn
                  v-else-if="!interaction.isPreview"
                  class="decline-button bold-face mr-4"
                  color="error !important"
                  role="button"
                  :loading="isWaitingDecline(interaction)"
                  :disabled="isWaitingEvent(interaction) || isOfflineModeEnabled"
                  @click="declineInteraction(interaction)"
                >
                  <v-icon class="icon">{{ getInteractionDeclineIcon(interaction) }}</v-icon>
                  <span v-once>{{ $t('actions.decline') }}</span>
                </v-btn>
                <v-btn
                  v-if="interaction.isPreview"
                  class="bold-face"
                  color="success"
                  role="button"
                  @click="acceptInteraction(interaction)"
                >
                  <span v-once>{{ $t('actions.startCall') }}</span>
                </v-btn>
                <v-btn
                  v-if="useBrowserToAnswer && interaction.isDirectCall"
                  class="bold-face pulse"
                  color="success"
                  role="button"
                  @click="acceptDirectCallInteraction(interaction)"
                >
                  <v-icon class="icon">{{ getInteractionAcceptIcon(interaction) }}</v-icon>
                  <span v-once>{{ $t('actions.accept') }}</span>
                </v-btn>
                <v-btn
                  v-else-if="
                    useBrowserToAnswer ||
                      !interaction.isTelephony ||
                      (interaction.isTelephony && isHPBXAndAcceptEnabled)
                  "
                  class="bold-face"
                  color="success"
                  role="button"
                  :class="{ pulse: !isWaitingEvent(interaction) }"
                  :loading="isWaitingAccept(interaction)"
                  :disabled="isWaitingEvent(interaction) || isOfflineModeEnabled"
                  @click="acceptInteraction(interaction)"
                >
                  <v-icon class="icon">{{ getInteractionAcceptIcon(interaction) }}</v-icon>
                  <span v-once>{{ $t('actions.accept') }}</span>
                </v-btn>
              </div>
            </div>
          </section>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>
    <ecs-add-note-dialog ref="addNote" />
  </section>
</template>

<script>
import moment from 'moment';
import { mapActions, mapGetters } from 'vuex';
import EcsInteractionTimerMixin from '@/mixins/ecs-interaction-mixin/EcsInteractionTimerMixin';
import InteractionService from '@/services/interactionService';
import EcsInteractionQueueWaitTimeMixin from '@/mixins/ecs-interaction-mixin/EcsInteractionQueueWaitTimeMixin';
import EcsQuickCrmContactInfo from '@/components/ecs-quick-crm-contact-info/EcsQuickCrmContactInfo';
import { splitCamelCasedWords } from '@/utils';
import EcsAddNoteDialog from '@/components/ecs-add-note-dialog/EcsAddNoteDialog';
import EcsSoftPhoneMixin from '@/components/ecs-soft-phone/EcsSoftPhoneMixin';
import EcsInteractionActionsMixin from '@/mixins/ecs-interaction-mixin/EcsInteractionActionsMixin';
import EcsQuickInteractionDetails from '@/components/ecs-quick-interaction-details/EcsQuickInteractionDetails.vue';

export default {
  name: 'EcsIncomingInteractionManager',

  components: { EcsQuickInteractionDetails, EcsAddNoteDialog, EcsQuickCrmContactInfo },

  filters: {
    displayName(interaction) {
      return !interaction.isInternal && interaction.isPendingConsult
        ? interaction.consultRequestorDisplayName
        : interaction.formattedCustomerFullName;
    }
  },

  mixins: [EcsInteractionTimerMixin, EcsInteractionQueueWaitTimeMixin, EcsSoftPhoneMixin, EcsInteractionActionsMixin],

  data() {
    return {
      previewTimer: 30,
      previewTimerReference: null,
      interactionTimers: {},
      open: [],
      showCrmInfo: false,
      showInteractionVariables: false,
      selectedPostponeId: '',
      postponeLoading: false,
      setDispositionCodeLoading: false,
      isPostponing: false
    };
  },

  computed: {
    ...mapGetters('AGENT', ['loggedInAgent']),

    ...mapGetters('EVENT_TIMING', ['isWaitingForEvent']),

    ...mapGetters('INTERACTIONS', ['getPendingInteractions']),

    ...mapGetters('SETTINGS', ['isOfflineModeEnabled', 'preferencesSettings']),

    ...mapGetters('ORGANIZATION', ['postponeOptions', 'getInteractionDetailsDisplayTemplateForBP']),

    ...mapGetters('INTERACTIONS/recordings', ['getRecordingByInteractionId']),

    pendingInteractions() {
      return this.getPendingInteractions.slice().reverse();
    },

    isWaitingPostponeEvent({ interactionId }) {
      return this.isWaitingForEvent({
        interactionId,
        constraintNames: ['INTERACTION_POSTPONED_EVENT']
      });
    }
  },

  watch: {
    getPendingInteractions() {
      this.startInteractionTimer();
    },

    pendingInteractions() {
      const offeredInteractions = this.pendingInteractions.some(interaction => interaction.isOffered);
      this.showInteractionVariables = this.preferencesSettings.autoOpenInteractionDetails;

      if (!offeredInteractions) {
        this.$ringtoneManager.stopInteractionRingtone(true);
      }

      if (this.pendingInteractions.length && this.pendingInteractions[0].isPreview) {
        this.previewTimer = this.pendingInteractions[0].previewTime || 60;
        clearInterval(this.previewTimerReference);
        this.previewTimerReference = setInterval(() => {
          this.previewTimer -= this.previewTimer > 0 ? 1 : 0;
        }, 1000);
      } else {
        this.previewTimer = 60;
      }
    }
  },

  mounted() {
    this.startInteractionTimer(this.getPendingInteractions);
    // TODO: This is what is making each incoming interaction component re-render every second
    // - maybe just $forceUpdate the timer and not the whole thing?
    this.$on('remaining-time-update', () => this.$forceUpdate());
  },

  beforeDestroy() {
    this.$off('remaining-time-update');
  },

  methods: {
    ...mapActions('EVENT_TIMING', ['addEventTimingConstraint', 'clearEventTimingConstraint']),

    ...mapActions('WINDOWS', ['setFocusedInteractionId']),

    isWaitingEvent({ interactionId }) {
      return this.isWaitingForEvent({ interactionId });
    },

    isWaitingAccept(interaction) {
      return this.isWaitingForEvent({
        interactionId: interaction.interactionId,
        constraintNames: [interaction.isTelephony ? 'INTERACTION_ANSWERED_EVENT' : 'INTERACTION_ACCEPTED_EVENT']
      });
    },

    isWaitingDecline({ interactionId }) {
      return this.isWaitingForEvent({
        interactionId,
        constraintNames: ['INTERACTION_REMOVED_FROM_AGENT_EVENT']
      });
    },

    recordingTranscription(interaction) {
      return (
        interaction.callbackRecordingTranscription ||
        this.getRecordingByInteractionId(interaction.interactionId).callbackRecordingTranscription ||
        'Unavailable.'
      );
    },

    recordingFile(interaction) {
      return (
        interaction.callbackRecordingFile ||
        this.getRecordingByInteractionId(interaction.interactionId).callbackRecordingFile
      );
    },

    completionStatusList(interaction) {
      return (interaction.completionStatuses || []).map(dispositionCode => {
        return {
          text: splitCamelCasedWords(dispositionCode.name),
          value: dispositionCode.value,
          callback: () => this.setPendingStatus(interaction.interactionId, dispositionCode.name)
        };
      });
    },

    async setPendingStatus(interactionId, pendingStatusName) {
      const result = {};
      let delegateToSupervisorRemark;

      if (pendingStatusName === 'DelegateToSupervisor') {
        delegateToSupervisorRemark = await this.$refs.addNote.open({
          title: this.$t('delegateToSupervisor.title'),
          description: this.$t('delegateToSupervisor.description'),
          placeholder: this.$t('delegateToSupervisor.placeholder'),
          confirmButtonText: this.$t('delegateToSupervisor.confirmButtonText'),
          cancelButtonText: this.$t('delegateToSupervisor.cancelButtonText')
        });
      }

      if (delegateToSupervisorRemark === false) {
        return this.$root.$emit('snackbar-message', {
          message: this.$t('delegateToSupervisor.message'),
          type: 'info'
        });
      }

      InteractionService.updatePendingStatus(interactionId, {
        pendingStatusName,
        delegateToSupervisorRemark: delegateToSupervisorRemark || undefined
      })
        .then(() => {
          result.message = this.$t('dispositionCode.successfullyUpdated');
          result.type = 'success';
        })
        .catch(() => {
          result.message = this.$t('dispositionCode.updateError');
          result.type = 'error';
        })
        .finally(() => {
          this.$root.$emit('snackbar-message', result);
        });
    },

    postponeOptionsList(interactionId, businessProcessId) {
      const postponeOptions = (this.postponeOptions(businessProcessId) || []).map(option => {
        return {
          text: splitCamelCasedWords(option.name),
          value: option.id,
          callback: () => this.postponeInteraction(interactionId, option.id)
        };
      });

      if (postponeOptions.length && !postponeOptions.map(o => o.value).includes(this.selectedPostponeId)) {
        this.selectedPostponeId = postponeOptions[0].value;
      }

      return postponeOptions;
    },

    postponeInteraction(interactionId, postponeOptionId) {
      const snackbarMessage = message => this.$root.$emit('snackbar-message', message);
      this.addEventTimingConstraint({
        interactionId,
        constraintNames: ['INTERACTION_POSTPONED_EVENT']
      });

      this.postponeLoading = true;
      InteractionService.postponeInteraction(interactionId, postponeOptionId)
        .catch(() => snackbarMessage({ message: `The operation could not be completed.`, type: 'error' }))
        .finally(() => (this.postponeLoading = false));
    },

    // --- Display (Render) Methods ---
    getInteractionAcceptIcon(interaction) {
      let icon = 'mdi-check'; // default
      if (interaction.isTelephony) {
        icon = 'mdi-phone';
      } else if (interaction.isChat) {
        icon = 'mdi-message-reply-text';
      } else if (interaction.isEmail) {
        icon = 'mdi-email-open';
      }
      return icon;
    },

    getInteractionDeclineIcon(interaction) {
      let icon = 'mdi-close'; // default
      if (interaction.isTelephony) {
        icon = 'mdi-phone-hangup';
      } else if (interaction.isChat) {
        icon = 'mdi-message-bulleted-off';
      } else if (interaction.isEmail) {
        icon = 'mdi-email';
      }
      return icon;
    },
    getInteractionTitleIcon(interaction) {
      let icon = 'mdi-lightbulb-on'; // default
      if (interaction.isPreview) {
        icon = 'mdi-motion-play';
      } else if (interaction.isTelephony) {
        icon = 'mdi-phone-ring';
      } else if (interaction.isChat) {
        icon = 'mdi-message-processing';
      } else if (interaction.isEmail) {
        icon = 'mdi-email';
      }
      return icon;
    },
    getInteractionTitle(interaction) {
      let title = '';
      if (interaction.isTelephony) {
        if (interaction.isCallback) {
          title = this.$t('callbackTelephonyInteraction', [
            interaction.routing.businessProcessName,
            interaction.routing.origin,
            interaction.formattedCustomerFullName
          ]);
        } else if (interaction.isInternal) {
          title = this.$t('incomingInternalCall', [interaction.formattedCustomerFullName]);
        } else if (interaction.isPendingConsult) {
          title = this.$t('incomingConsult', [
            interaction.consultRequestorDisplayName,
            interaction.formattedCustomerFullName
          ]);
        } else if (interaction.isCampaign) {
          title = this.$t('incomingCampaignInteraction', [
            interaction.routing.businessProcessName,
            interaction.routing.destination,
            interaction.routing.campaignName
          ]);
        } else if (interaction.isDirectCall) {
          title = this.$t('incomingDirectCall', [interaction.customerName, interaction.routing.origin]);
        } else {
          title = this.$t('incomingTelephonyInteraction', [
            interaction.routing.businessProcessName,
            interaction.routing.destination,
            interaction.formattedCustomerFullName
          ]);
        }
      } else if (interaction.isChat) {
        if (interaction.isInternal) {
          title = this.$t('incomingInternalChat', [interaction.formattedCustomerFullName]);
        } else if (interaction.isPendingConsult) {
          title = this.$t('incomingConsult', [
            interaction.consultRequestorDisplayName,
            interaction.formattedCustomerFullName
          ]);
        } else {
          title = this.$t('incomingChatInteraction', [
            interaction.routing.businessProcessName,
            interaction.formattedCustomerFullName
          ]);
        }
      } else if (interaction.isEmail) {
        title = this.$t('incomingEmailInteraction', { businessProcess: interaction.routing.businessProcessName });
      } else if (interaction.isSms) {
        title = this.$t('incomingSmsInteraction', [
          interaction.routing.businessProcessName,
          interaction.formattedCustomerFullName
        ]);
      }
      return title;
    },

    // --- Basic Functionality ---
    /**
     * Accepts an incoming interaction and removes the active interaction timer
     * @param {EcsInteraction} interaction
     */
    acceptInteraction({ interactionId, isTelephony }) {
      if (this.isWaitingForEvent({ interactionId })) {
        return;
      }

      this.addEventTimingConstraint({
        interactionId,
        constraintNames: [isTelephony ? 'INTERACTION_ANSWERED_EVENT' : 'INTERACTION_ACCEPTED_EVENT']
      });

      this.stopInteractionTimer(this.interactionTimers[interactionId]);
      delete this.interactionTimers[interactionId];

      InteractionService.acceptInteraction(interactionId)
        .then(() => this.setFocusedInteractionId(interactionId))
        .catch(() => this.$root.$emit('restore-active-interactions'))
        .finally(() => this.clearEventTimingConstraint({ interactionId }));
    },

    /**
     * Collapses all open interaction banners
     */
    collapseAll() {
      this.open = [];
    },

    /**
     * Declines an incoming interaction and removes the active interaction timer
     * @param {EcsInteraction} interaction
     */
    declineInteraction({ interactionId }) {
      if (this.isWaitingForEvent({ interactionId })) {
        return;
      }
      this.addEventTimingConstraint({
        interactionId,
        constraintNames: ['INTERACTION_REMOVED_FROM_AGENT_EVENT']
      });
      this.stopInteractionTimer(this.interactionTimers[interactionId]);
      delete this.interactionTimers[interactionId];
      InteractionService.declineInteraction(interactionId)
        .catch(() => this.$root.$emit('restore-active-interactions'))
        .finally(() => this.clearEventTimingConstraint({ interactionId }));
    },

    timerInterval: (interaction, self) =>
      setInterval(() => {
        const thisTimer = self.interactionTimers[interaction.interactionId];
        if (thisTimer.remaining > 0) {
          const now = moment.utc();
          thisTimer.remaining = thisTimer.expirationTimestamp.diff(now, 'seconds');
          self.$emit('remaining-time-update', thisTimer.remaining);
        } else {
          clearInterval(thisTimer.interval);
        }
      }, 1000),

    startInteractionTimer(interactions = this.getPendingInteractions || []) {
      if (interactions.length > 0) {
        const timerKeys = Object.keys(this.interactionTimers);
        const newInteractions = interactions.filter(i => !timerKeys.includes(i.interactionId));
        this.$ringtoneManager.startInteractionRingtone(newInteractions[0]);
        newInteractions.forEach(interaction => {
          this.interactionTimers[interaction.interactionId] = this.createTimer(interaction);
          this.interactionTimers[interaction.interactionId].interval = this.timerInterval(interaction, this);
        });
        if (interactions.length === 1) {
          this.open = [0];
        }
        if (interactions.length >= 3) {
          this.collapseAll();
        }
      } else {
        this.$ringtoneManager.stopInteractionRingtone();
        const timerKeys = Object.keys(this.interactionTimers);
        timerKeys.forEach(key => {
          clearInterval(this.interactionTimers[key].interval);
        });
        this.interactionTimers = {};
      }
      this.$root.$emit('change-favicon');
    }
  }
};
</script>

<style scoped>
.preview-container {
  display: block;
  width: 484px;
  margin-top: 12px;
}
.preview-transcription {
  text-transform: initial;
}
.preview {
  border-radius: 4px;
  overflow: hidden;
  margin: 4px 0;
  width: 468px;
}
.preview-label {
  font-size: 12px;
  color: #fff;
  background: #4caf50;
  padding: 0 0 0 16px;
  display: inline-block;
  border-radius: 4px 0 0 4px;
  height: 36px;
  line-height: 36px;
  white-space: nowrap;
}
.preview-label-timer {
  font-size: 12px;
  color: #fff;
  background: #4caf50;
  padding: 0 16px 0 4px;
  display: inline-block;
  border-radius: 20px;
  height: 36px;
  line-height: 36px;
  white-space: nowrap;
  margin: 0 8px;
}
.preview-label-timer .timer {
  margin-top: -2px;
}
.preview-label-timer .timer >>> .v-progress-circular__underlay {
  stroke: #fff7;
}
.preview-label-timer .label {
  padding: 0 0 0 8px;
  display: inline-block;
}
.ecs-incoming-interactions-container {
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  z-index: 140;
}
.interaction-title {
  font-size: 14px;
  line-height: 26px;
  text-transform: uppercase;
  float: left;
}
.header-information {
  width: fit-content;
  display: flex;
  justify-content: flex-end;
}
.collapsed-actions {
  width: fit-content;
  display: flex;
  flex-flow: row;
  flex-wrap: nowrap;
}
.interaction-subtitle {
  font-size: 12px;
  text-transform: none;
}
.missed .actions {
  display: none;
}
button {
  border-radius: 8px;
}
button.secondary-action {
  background-color: #fff1;
}
button .icon {
  width: 20px;
  height: 20px;
  font-size: 20px;
  margin-right: 8px;
}
.interaction-content {
  width: 100%;
  margin: 0;
  padding: 0 24px 24px;
  display: flex;
  flex-flow: column;
}
.primary-content {
  width: 100%;
  padding-bottom: 12px;
}
.secondary-content {
  display: grid;
  width: 100%;
  grid-template-columns: auto max-content;
}
.avatar-remarks-info {
  grid-column: 2;
  grid-row: 1;
  justify-self: right;
  text-align: right;
}
.actions {
  width: fit-content;
  text-align: right;
}
.primary-display-name {
  font-size: 36px;
  text-align: left;
}
.business-process {
  font-size: 12px;
  text-align: left;
  padding: 8px 0;
}
.pulse {
  animation: pulse 1s ease-in-out infinite both;
}
.prop-label {
  opacity: 0.75;
  font-size: 12px;
  padding-right: 4px;
  min-width: 120px;
  display: inline-block;
}
.prop-value {
  font-size: 14px;
  font-weight: bold;
}
.title-additional-info {
  font-size: 12px;
  opacity: 0.5;
}

.audio-preview {
  display: grid;
  grid-template-columns: min-content auto;
}

audio.preview-audio::-webkit-media-controls-enclosure {
  background-color: #a34da9 !important;
  border-radius: 0 4px 4px 0 !important;
}

audio {
  width: calc(100% - 16px);
  max-width: 360px;
  height: 36px;
}

audio::-webkit-media-controls-enclosure {
  border-radius: 4px;
  filter: invert(1);
}

.show-crm-btn,
.show-int-details-btn {
  margin-left: 8px;
  border-radius: 4px;
}

.postpone,
.disposeInteraction {
  display: inline-block;
  min-width: 300px;
  margin-right: 12px;
  transform: translateY(1px);
}

@keyframes pulse {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(0.9);
  }
  100% {
    transform: scale(1);
  }
}
@media only screen and (max-width: 630px) {
  .secondary-content {
    flex-flow: column;
  }
  .demands {
    width: 100%;
    margin-bottom: 8px;
  }
  .actions {
    width: 100%;
  }
  .decline-button {
    margin-right: 0 !important;
  }
}
</style>

<style>
.postpone .v-input__control,
.disposeInteraction .v-input__control {
  min-height: 36px !important;
  border-radius: 8px 0 0 8px !important;
}
.postpone .v-select__selections,
.postpone .v-btn,
.postpone .v-select__slot,
.postpone .v-input__append-inner,
.disposeInteraction .v-select__selections,
.disposeInteraction .v-btn,
.disposeInteraction .v-select__slot,
.disposeInteraction .v-input__append-inner {
  height: 36px !important;
}
.postpone .v-btn,
.disposeInteraction .v-btn {
  border-radius: 8px 0 0 8px !important;
}
.postpone .v-input--is-focused .v-input__slot,
.disposeInteraction .v-input--is-focused .v-input__slot {
  border-radius: 8px !important;
}
.postpone .v-input__slot,
.disposeInteraction .v-input__slot {
  padding: 0 4px 0 0 !important;
  border-radius: 8px;
  border-width: 1px !important;
  box-shadow: none !important;
  background-color: var(--v-surface-base) !important;
}
.theme--dark.postpone .v-input__slot,
.theme--dark.disposeInteraction .v-input__slot {
  background-color: #0004 !important;
}
</style>
